import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, Navigate, useParams } from 'react-router-dom';
import '../Dashboard/matchup.css';
import { Alert, Row, Col } from 'react-bootstrap';
import PlayerPanel from '../Dashboard/playerPanel';
import UserHeader from '../UserHeader';
import TeamLegend from '../Dashboard/teamLegend';
import { highlightColumn, highlightColumnColor, checkHealthScoreStatus, getPlayerCategories, sortTopNBAPlayers } from '../utils/dashboardCalculations';
import { sortArrayOfObj } from '../utils/utils';

function CricketMatchup(props) {
	const { user } = props;
	let { id: gameID } = useParams();

	useEffect(() => {
		window.scrollTo(0, 0);
		let { dispatch, match, healthScores, assignCurrentSport } = props;
		assignCurrentSport();
		if (!props?.user?.login) return;
		if (!match.match || gameID) {
			dispatch({ type: 'GET_MATCH', payload: { gameID } });
			if (!healthScores.categoryAverages) {
				dispatch({ type: 'GET_HEALTH_SCORES', payload: { gameID } });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [showAwayRoster, handleShowAwayRoster] = useState(false);
	const [showHomeRoster, handleShowHomeRoster] = useState(false);
	const [showScorecardSection, setShowScorecardSection] = useState(true);
	const [showNotesSection, setShowNotesSection] = useState(true);

	const toggleRosterView = (type) => {
		if (type === 'away') {
			handleShowAwayRoster(!showAwayRoster);
		} else if (type === 'home') {
			handleShowHomeRoster(!showHomeRoster);
		}
	};

	const { match, schedules, hideUserHeader, toggleScheduleView, healthScores, teams } = props;
	const title = 'Health Scores for Team Matchups';

	if (!user?.isLoading && !user?.login) {
		return <Navigate to='/' replace />;
	}

	if (match.isLoading || healthScores.isLoading || user.isLoading)
		return (
			<div id='fullHeight'>
				<div id='loading' />
			</div>
		);
	if (!match.awayPlayers || !match.homePlayers) {
		return (
			<React.Fragment>
				<div className='gameMainDiv'>
					<UserHeader matchup={true} backgroundColor={'#f7f7f7'} title={title} />
					<h1 className='bold text-center'>{match.error}</h1>
				</div>
			</React.Fragment>
		);
	}
	const game = match?.match[0];
	let { awayPlayers, homePlayers } = match;

	const awayTeam = teams?.data?.teamInfo?.find((x) => x.team_id === game?.away_team_id);
	const homeTeam = teams?.data?.teamInfo?.find((x) => x.team_id === game?.home_team_id);

	const reboundersAway = awayPlayers && getPlayerCategories(awayPlayers, 'rebounders', healthScores, awayTeam);
	const scorersAway = awayPlayers && getPlayerCategories(awayPlayers, 'scorers', healthScores, awayTeam);
	const defenseAway = awayPlayers && getPlayerCategories(awayPlayers, 'defense', healthScores, awayTeam);
	const reboundersHome = homePlayers && getPlayerCategories(homePlayers, 'rebounders', healthScores, homeTeam);
	const scorersHome = homePlayers && getPlayerCategories(homePlayers, 'scorers', healthScores, homeTeam);
	const defenseHome = homePlayers && getPlayerCategories(homePlayers, 'defense', healthScores, homeTeam);

	let topFourAway = awayPlayers && sortTopNBAPlayers(awayPlayers);
	let topFourHome = homePlayers && sortTopNBAPlayers(homePlayers);

	// Parse roster players from API (homeRosterPlayers, awayRosterPlayers)
	const awayRosterPlayersResp = match?.awayRosterPlayers || [];
	const homeRosterPlayersResp = match?.homeRosterPlayers || [];

	// Helper to parse roster JSON and pick the correct team by full name
	const parseTeamRoster = (rows, teamFullName) => {
		if (!rows || !rows.length) return null;
		// Try to find a response JSON in any row
		const jsonStr = (rows.find(r => r && r.response && typeof r.response === 'string') || {})?.response;
		if (false && jsonStr) {
			try {
				const data = JSON.parse(jsonStr);
				const teams = Array.isArray(data?.teams) ? data.teams : [];
				const stripDiacritics = (s) => (s || '').normalize('NFD').replace(/\p{Diacritic}+/gu, '');
				const normBasic = (s) => (s || '').toString().trim().toLowerCase();
				const normalized = (s) => {
					const t = stripDiacritics(s).toLowerCase();
					return t.replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
				};
				const target = normalized(teamFullName);
				const teamObj =
					teams.find(t => normalized(t?.team_name) === target) ||
					teams.find(t => normalized(`${t?.team_name}`)?.includes(normalized(game?.away_team_name)) || normalized(`${t?.team_name}`)?.includes(normalized(game?.home_team_name))) ||
					teams[0];
					if (teamObj) {
						// Build an index from SQL rows to enrich JSON players with IDs and details
					const nameKey = (obj) => normalized(obj?.full_name || obj?.player_name || obj?.nickname || obj?.name);
					const altKeys = (r) => {
						const keys = new Set();
						[key => key].forEach(() => {}); // no-op to satisfy lints for unused helpers
						[ r.full_name, r.player_name, r.nickname ].forEach(n => { if (n) keys.add(nameKey({ full_name: n })); });
						return Array.from(keys);
					};
					const rowIndex = new Map();
					(rows || []).forEach(r => {
						altKeys(r).forEach(k => { if (k && !rowIndex.has(k)) rowIndex.set(k, r); });
					});
						const enrich = (arr) => (arr || []).map(p => {
							const key = nameKey(p);
							let rowMatch = rowIndex.get(key);
						if (!rowMatch && p?.full_name) {
							// try a looser match: remove middle parts or compare startsWith
							const parts = normalized(p.full_name).split(' ');
							if (parts.length > 1) {
								const tryKey = `${parts[0]} ${parts[parts.length - 1]}`;
								rowMatch = rowIndex.get(tryKey) || rowIndex.get(parts[0]) || rowIndex.get(parts[parts.length - 1]);
							}
						}
							return {
								...p,
								full_name: p.full_name || p.player_name || rowMatch?.full_name || rowMatch?.player_name || p.name,
								player_id: p.player_id || rowMatch?.player_id,
								// Always prefer DB row fields for position and gaming_score
								position: rowMatch?.position || rowMatch?.position_map || rowMatch?.position_category || p.position,
								gaming_score: rowMatch?.gaming_score ?? p.gaming_score,
							};
						});
					return {
						team_name: teamObj.team_name,
						projected_starters: enrich(teamObj.projected_starters),
						active_roster: enrich(teamObj.active_roster),
						inactive_or_injured: enrich(teamObj.inactive_or_injured)
					};
				}
			} catch (e) {
				// fall back to building from rows
			}
		}
		// Build bundles directly from SQL rows (roaster_players): use player_name, player_id, position, gaming_score
		const toPlayer = (r) => ({
			player_id: r.player_id,
			player_name: r.player_name,
			role: r.role || (r.depth_chart_order === 1 ? 'Starter' : 'Bench'),
			position: r.position,
			position_category: r.position_category,
			hpf_total: r.hpf_total,
			irc_total: r.irc_total,
			gaming_score: r.gaming_score,
			full_name: r.player_name
		});
		const starters = rows.filter(r => (r.role && String(r.role).toLowerCase() === 'starter') || r.depth_chart_order === 1).map(toPlayer);
		const inactive = rows.filter(r => {
			const s = (r.status || r.injury_status || '').toString().toLowerCase();
			return s && s !== 'active';
		}).map(toPlayer);
		const active = rows.filter(r => {
			const s = (r.status || r.injury_status || '').toString().toLowerCase();
			return !s || s === 'active';
		}).map(toPlayer);
		if (!starters.length && !active.length && !inactive.length) return null;
		return {
			team_name: teamFullName,
			projected_starters: starters,
			active_roster: active,
			inactive_or_injured: inactive
		};
	};

	const awayTeamFullName = [game?.away_team_city, game?.away_team_name].filter(Boolean).join(' ');
	const homeTeamFullName = [game?.home_team_city, game?.home_team_name].filter(Boolean).join(' ');
	const awayRosterBundle = parseTeamRoster(awayRosterPlayersResp, awayTeamFullName);
	const homeRosterBundle = parseTeamRoster(homeRosterPlayersResp, homeTeamFullName);

	const getGroupedRosterPlayers = (players) => {
		const roster = Array.isArray(players) ? players : [];
		const normalize = (s) => (s || '').toString().trim().toLowerCase();
		const categoryForPosition = (pos) => {
			const p = normalize(pos);
			if (!p) return 'batters';
			if (p.includes('wk') || p.includes('wicket')) return 'wicket_keeper';
			if (p.includes('all')) return 'all_rounders';
			if (p.includes('bowl')) return 'bowlers';
			return 'batters';
		};
		const groups = {
			batters: [],
			bowlers: [],
			all_rounders: [],
			wicket_keeper: []
		};
		roster.forEach((pl) => {
			const key = categoryForPosition(pl?.position);
			groups[key].push(pl);
		});
		return groups;
	};

	const averageScore = (players) => {
		const arr = Array.isArray(players) ? players : [];
		if (!arr.length) return null;
		const sum = arr.reduce((acc, p) => acc + (Number(p?.gaming_score) || 10), 0);
		return sum / arr.length;
	};

	const panelStatusForPlayers = (players) => {
		const avg = averageScore(players);
		if (avg === null) return checkHealthScoreStatus(10, [], 'team');
		return checkHealthScoreStatus(avg, players, 'team');
	};

	const awayRoster =
		awayPlayers &&
		awayPlayers.filter((x) => {
			if (x.active === 1) {
				return x;
			}
			return;
		});
	const homeRoster =
		homePlayers &&
		homePlayers.filter((x) => {
			if (x.active === 1) {
				return x;
			}
			return;
		});

	awayRoster.sort(sortArrayOfObj('player_name', 'asc'));
	homeRoster.sort(sortArrayOfObj('player_name', 'asc'));

	const homeHealthScore = game && game.home_team_gaming_score;
	const awayHealthScore = game && game.away_team_gaming_score;
	const homeStatusText = checkHealthScoreStatus(homeHealthScore, false, 'team');
	const awayStatusText = checkHealthScoreStatus(awayHealthScore, false, 'team');
	let teamHighlightShadowAway;
	let teamHighlightShadowHome;
	const highlightCol = highlightColumn(homeHealthScore, awayHealthScore);
	if (highlightCol === 'away') {
		teamHighlightShadowAway = 'teamShadowHighlight';
	} else if (highlightCol === 'home') {
		teamHighlightShadowHome = 'teamShadowHighlight';
	}
	const homeHighlightColor = highlightColumnColor(homeHealthScore, 'teamHighlight');
	const awayHighlightColor = highlightColumnColor(awayHealthScore, 'teamHighlight');
	const offenseDesc = 'This category reflects the avg health score of all core offensive players injuries on a specific team. It assesses their injury impact on performance as a group.';
	const defenseDesc = "This category reflects the avg health score of all core defensive players' injuries on a specific team. It assesses their injury impact on performance as a group.";
	const offenseDefenseDesc = "This category reflects the avg health score of all core players' injuries on a specific team. It assesses their injury impact on performance as a group.";
	const matchDate = game?.date_time?.date || game?.date_time || game?.date_start_utc;
	const formattedMatchDate = matchDate ? new Date(matchDate).toLocaleString() : 'TBD';
	const matchStatus = game?.status || game?.status_str || 'N/A';
	const matchResult = game?.result || game?.status_note || 'N/A';
	
	// Debug: Log the game object to check if weather_json and pitch_json exist
	console.log('Game object:', game);
	console.log('weather_json:', game?.weather_json);
	console.log('pitch_json:', game?.pitch_json);
	const statusTone = /complete|won|loss|result|finished/i.test(String(matchStatus))
		? { bg: '#e8f7ee', color: '#1f7a3e' }
		: /live|progress|in.?play/i.test(String(matchStatus))
			? { bg: '#fff4e5', color: '#9a5b00' }
			: { bg: '#eef3ff', color: '#1d4ed8' };
	const scorecard = match?.scorecard || {};
	const scorecardInnings = scorecard?.innings || [];
	const scorecardNotes = scorecard?.notes || [];
	const scorecardPlayers = scorecard?.players || [];
	const resolveTeamName = (teamId) => {
		if (!teamId) return 'Team';
		if (String(teamId) === String(game?.away_team_id)) return game?.away_team_name || 'Away Team';
		if (String(teamId) === String(game?.home_team_id)) return game?.home_team_name || 'Home Team';
		return `Team ${teamId}`;
	};
	const asArray = (value) => (Array.isArray(value) ? value : []);
	const pickValue = (obj, keys, fallback = '-') => {
		for (const key of keys) {
			if (obj && obj[key] !== undefined && obj[key] !== null && obj[key] !== '') return obj[key];
		}
		return fallback;
	};
	const resolvePlayerName = (player) =>
		player?.nickname ||
		player?.player_name ||
		player?.full_name ||
		player?.name ||
		'';


	return (
		<section className='gameMainSection'>
			<div className='gameMainDiv'>
				<UserHeader matchup={true} backgroundColor={'#f7f7f7'} title={title} hideUserHeader={hideUserHeader} toggleScheduleView={() => toggleScheduleView()} game={game} />
				<div className='gameBoxShadow'>
					<Row className='gameSpreadHeaderRow'>
						<Col lg={6} className={`gameSpreadHeaderCol ${awayHighlightColor}`}>
							<div className='gameSpreadHeaderDiv'>
								<React.Fragment>
									<p className='gameSpreadHeaderSpread'>
										<span style={{ paddingRight: '20px' }}>{awayStatusText.status}</span>
										{awayHealthScore}
									</p>
									<p className='gameSpreadHeaderDescription'>Team Health Score</p>
								</React.Fragment>
							</div>
						</Col>
						<Col lg={6} className={`gameSpreadHeaderCol ${homeHighlightColor}`}>
							<div className='gameSpreadHeaderDiv'>
								<React.Fragment>
									<p className='gameSpreadHeaderDescription'>Team Health Score</p>
									<p className='gameSpreadHeaderSpread pull-right'>
										<span style={{ paddingRight: '20px' }}>{homeStatusText.status}</span>
										{homeHealthScore}
									</p>
								</React.Fragment>
							</div>
						</Col>
					</Row>
					<Row className='gameTeamHeaderRow'>
						<Col lg={6} className={`gameTeamHeaderCol ${teamHighlightShadowAway} col-xs-12`}>
							<div className='gameTeamHeaderDiv'>
								<p className={`gameTeamHeaderSpread bold ${awayHighlightColor}`}>{awayHealthScore}</p>
								<div className='gameTeamHeaderLogoDiv gameTeamHeaderLogoAwayDiv'>
									<img src={game.away_team_logo} alt='Team Logo' className='gameTeamLogo' />
									<p className='gameTeamHeaderName gameTeamHeaderNameAway'>{game.away_team_city + ' ' + game.away_team_name}</p>
								</div>
							</div>
						</Col>
						<Col lg={6} className={`gameTeamHeaderCol ${teamHighlightShadowHome} col-xs-12`}>
							<div className='gameTeamHeaderDiv gameTeamHeaderDivHome'>
								<div className='gameTeamHeaderLogoDiv gameTeamHeaderLogoRightDiv'>
								<p className='gameTeamHeaderName gameTeamHeaderNameHome'>@ {game.home_team_city + ' ' + game.home_team_name}</p>
								<img src={game.home_team_logo} alt='Team Logo' className='gameTeamLogo' />
								</div>
								<p className={`gameTeamHeaderSpread gameTeamHeaderSpreadRight bold ${homeHighlightColor}`}>{homeHealthScore}</p>
							</div>
						</Col>
					</Row>
					<TeamLegend />
					{game.game_description ? (
						<Row className='gameDescriptionRow'>
							<Col lg={12} className='gameDescriptionCol'>
								<p className='gameDescription'>{game.game_description}</p>
							</Col>
						</Row>
					) : null}
					<Row style={{ padding: '10px 20px 20px 20px' }}>
						<Col lg={12}>
							<div style={{ background: '#f7f7f7', border: '1px solid #e6e6e6', borderRadius: '10px', padding: '14px 16px' }}>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
									<h4 style={{ margin: 0, fontWeight: 700 }}>IPL Match Details</h4>
									<span style={{ background: statusTone.bg, color: statusTone.color, borderRadius: '999px', padding: '4px 10px', fontSize: '12px', fontWeight: 700 }}>
										{matchStatus}
									</span>
								</div>
								<Row style={{ rowGap: '10px' }}>
									<Col lg={4} md={6} xs={12}>
										<div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: '8px', padding: '10px' }}>
											<p style={{ margin: 0, color: '#6b7280', fontSize: '12px', textTransform: 'uppercase' }}>Date & Time</p>
											<p style={{ margin: '4px 0 0 0', fontWeight: 600 }}>{formattedMatchDate}</p>
										</div>
									</Col>
									<Col lg={4} md={6} xs={12}>
										<div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: '8px', padding: '10px' }}>
											<p style={{ margin: 0, color: '#6b7280', fontSize: '12px', textTransform: 'uppercase' }}>Venue</p>
											<p style={{ margin: '4px 0 0 0', fontWeight: 600 }}>{[game?.venue_name, game?.venue_location].filter(Boolean).join(', ') || 'N/A'}</p>
										</div>
									</Col>
									<Col lg={4} md={6} xs={12}>
										<div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: '8px', padding: '10px' }}>
											<p style={{ margin: 0, color: '#6b7280', fontSize: '12px', textTransform: 'uppercase' }}>Match No / Format</p>
											<p style={{ margin: '4px 0 0 0', fontWeight: 600 }}>{game?.match_number || 'N/A'} / {game?.format_str || game?.format || 'N/A'}</p>
										</div>
									</Col>
									<Col lg={12} xs={12}>
										<div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: '8px', padding: '10px' }}>
											<p style={{ margin: 0, color: '#6b7280', fontSize: '12px', textTransform: 'uppercase' }}>Toss</p>
											<p style={{ margin: '4px 0 0 0', fontWeight: 600 }}>{game?.toss_text || 'N/A'}</p>
										</div>
									</Col>
									<Col lg={12} xs={12}>
										<div style={{ background: '#eef7ff', border: '1px solid #dbeafe', borderRadius: '8px', padding: '10px' }}>
											<p style={{ margin: 0, color: '#1e3a8a', fontSize: '12px', textTransform: 'uppercase' }}>Result</p>
											<p style={{ margin: '4px 0 0 0', fontWeight: 700 }}>{matchResult}</p>
										</div>
									</Col>
								</Row>
							</div>
						</Col>
					</Row>
					
					
					{user?.login ? (
						<>
							<div className='topFourPlayerDiv'>
								<h1 className='dashboardHowItWorksHeader'>Key Players</h1>
							</div>
							<Row>
								<Col lg={6} className='gameMatchupHeaderCol gameMatchupHeaderCol1'>
									<div className='matchupHeaderDiv'>
										<hr className='hrStyleMobile' />
										<h3 className='matchupHeaderTopMobile'>Away Team</h3>
										<hr className='hrStyleMobile' />
										<Row>
											{topFourAway?.length &&
												topFourAway?.map((players, i) => {
													return (
														<Col lg={6} key={i}>
															<Link
																className='routerLinkMatchup hover-delay'
																title={`${players.player_name}`}
																to={{
																	pathname: `/cricket/ipl/player/${players?.player_id}`,
																	playerID: players?.player_id
																}}
															>
																<div className='matchPlayerDiv'>
																	<div className='matchPlayerInfoDiv matchPlayerInfoTopDiv'>
																		<p className='matchPlayerInfoNameTop'>{players.nickname || players.player_name}</p>
																		<p className='matchPlayerInfoInjury'>
																			<span className='grey'>POSITION:</span>
																			<span className='matchPlayerInfo'>{players.position}</span>
																		</p>
																		<p className='matchPlayerInfoInjury'>
																			<span className='grey'>HEALTH SCORE:</span>
																			<span className='matchPlayerInfo'>{players.gaming_score || '10'}</span>
																		</p>
																	</div>
																</div>
															</Link>
														</Col>
													);
												})}
										</Row>
									</div>
								</Col>
								<Col lg={6} className='gameMatchupHeaderCol gameMatchupHeaderCol1'>
									<div className='matchupHeaderDiv'>
										<hr className='hrStyleMobile' />
										<h3 className='matchupHeaderTopMobile'>Home Team</h3>
										<hr className='hrStyleMobile' />
										<Row>
											{topFourHome?.length &&
												topFourHome?.map((players, i) => {
													return (
														<Col lg={6} key={i}>
															<Link
																className='routerLinkMatchup hover-delay'
																title={`${players.player_name}`}
																to={{
																	pathname: `/cricket/ipl/player/${players?.player_id}`,
																	playerID: players?.player_id
																}}
															>
																<div className='matchPlayerDiv'>
																	{/* <img
																		src={game.home_team_logo}
																		alt="Player"
																		className="matchPlayerImg displayTableCell"
																	/> */}
																	<div className='matchPlayerInfoDiv matchPlayerInfoTopDiv'>
																		<p className='matchPlayerInfoNameTop'>{players.nickname || players.player_name}</p>
																		<p className='matchPlayerInfoInjury'>
																			<span className='grey'>POSITION:</span>
																			<span className='matchPlayerInfo'>{players.position}</span>
																		</p>
																		<p className='matchPlayerInfoInjury'>
																			<span className='grey'>HEALTH SCORE:</span>
																			<span className='matchPlayerInfo'>{players.gaming_score || '10'}</span>
																		</p>
																	</div>
																</div>
															</Link>
														</Col>
													);
												})}
										</Row>
									</div>
								</Col>
							</Row>
						</>
					) : null}


					{(awayRosterBundle || homeRosterBundle) ? (
						<>
							<Row>
								<Col lg={12}>
									<h1 className='dashboardHowItWorksHeader'>Cricket Role Categories</h1>
								</Col>
							</Row>
							<Row className='gameMatchupRow'>
								{/* Away Roster */}
								<Col lg={6} className='gameMatchupHeaderCol gameMatchupHeaderCol1'>
									<div className='matchupHeaderDiv'>
										<Row>
											<Col lg={12} className='matchupHeaderCol'>
												<hr className='hrStyleMobile' />
												<p className='matchupMobileHeader'>{[game.away_team_city, game.away_team_name].filter(v => v && v !== 'null').join(' ')}</p>
												<hr className='hrStyleMobile' />
											</Col>
										</Row>
									</div>
									<div style={{ paddingBottom: '30px' }}>
										{(() => {
											const rosterPlayersRaw = awayRosterBundle?.active_roster || [];
											const hasPlayingFlag = Array.isArray(rosterPlayersRaw) && rosterPlayersRaw.some((p) => p && p.is_playing !== undefined && p.is_playing !== null);
											const playing = hasPlayingFlag
												? rosterPlayersRaw.filter((p) => Number(p?.is_playing) === 1)
												: rosterPlayersRaw;
											const rosterPlayers = Array.isArray(playing) ? playing.slice(0, 11) : [];
											if (!rosterPlayers.length) return null;
											const grouped = getGroupedRosterPlayers(rosterPlayers);
											return (
												<>
													<PlayerPanel id={101} healthScoreStatus={panelStatusForPlayers(grouped.batters)} players={grouped.batters} panelTitle='🏏 BATTERS' sportsName='cricket' teamLogo={game.away_team_logo} openAll={false} />
													<PlayerPanel id={102} healthScoreStatus={panelStatusForPlayers(grouped.bowlers)} players={grouped.bowlers} panelTitle='🔴 BOWLERS' sportsName='cricket' teamLogo={game.away_team_logo} openAll={false} />
													<PlayerPanel id={103} healthScoreStatus={panelStatusForPlayers(grouped.all_rounders)} players={grouped.all_rounders} panelTitle='ALL-ROUNDERS' sportsName='cricket' teamLogo={game.away_team_logo} openAll={false} />
													<PlayerPanel id={104} healthScoreStatus={panelStatusForPlayers(grouped.wicket_keeper)} players={grouped.wicket_keeper} panelTitle='🧤 WICKET-KEEPER' sportsName='cricket' teamLogo={game.away_team_logo} openAll={false} />
												</>
											);
										})()}
									</div>
								</Col>
								{/* Home Roster */}
								<Col lg={6} className='gameMatchupHeaderCol gameMatchupHeaderCol2'>
									<div className='matchupHeaderDiv'>
										<Row>
											<Col lg={12} className='matchupHeaderCol'>
												<hr className='hrStyleMobile' />
												<p className='matchupMobileHeader'>{[game.home_team_city, game.home_team_name].filter(v => v && v !== 'null').join(' ')}</p>
												<hr className='hrStyleMobile' />
											</Col>
										</Row>
									</div>
									<div style={{ paddingBottom: '30px' }}>
										{(() => {
											const rosterPlayersRaw = homeRosterBundle?.active_roster || [];
											const hasPlayingFlag = Array.isArray(rosterPlayersRaw) && rosterPlayersRaw.some((p) => p && p.is_playing !== undefined && p.is_playing !== null);
											const playing = hasPlayingFlag
												? rosterPlayersRaw.filter((p) => Number(p?.is_playing) === 1)
												: rosterPlayersRaw;
											const rosterPlayers = Array.isArray(playing) ? playing.slice(0, 11) : [];
											if (!rosterPlayers.length) return null;
											const grouped = getGroupedRosterPlayers(rosterPlayers);
											return (
												<>
													<PlayerPanel id={201} healthScoreStatus={panelStatusForPlayers(grouped.batters)} players={grouped.batters} panelTitle='🏏 BATTERS' sportsName='cricket' teamLogo={game.home_team_logo} openAll={false} />
													<PlayerPanel id={202} healthScoreStatus={panelStatusForPlayers(grouped.bowlers)} players={grouped.bowlers} panelTitle='🔴 BOWLERS' sportsName='cricket' teamLogo={game.home_team_logo} openAll={false} />
													<PlayerPanel id={203} healthScoreStatus={panelStatusForPlayers(grouped.all_rounders)} players={grouped.all_rounders} panelTitle='ALL-ROUNDERS' sportsName='cricket' teamLogo={game.home_team_logo} openAll={false} />
													<PlayerPanel id={204} healthScoreStatus={panelStatusForPlayers(grouped.wicket_keeper)} players={grouped.wicket_keeper} panelTitle='🧤 WICKET-KEEPER' sportsName='cricket' teamLogo={game.home_team_logo} openAll={false} />
												</>
											);
										})()}
									</div>
								</Col>
							</Row>
						</>
					) : null}


					{/* Weather Section */}
						{(game?.weather_json || game?.weather) ? (
							<Row style={{ padding: '0 20px 20px 20px' }}>
								<Col lg={12}>
								<div style={{ background: '#fff', border: '1px solid #e6e6e6', borderRadius: '8px', padding: '14px 16px' }}>
									<h5 style={{ margin: '0 0 12px 0', fontWeight: 700, color: '#374151' }}>Weather Conditions</h5>
									{(() => {
										try {
											let weatherData;
											if (game.weather_json) {
												weatherData = typeof game.weather_json === 'string' ? JSON.parse(game.weather_json) : game.weather_json;
											} else {
												// Fallback to individual weather fields if weather_json doesn't exist
												weatherData = {
													weather: game.weather,
													weather_desc: game.weather_desc,
													temp: game.temp,
													humidity: game.humidity,
													visibility: game.visibility,
													wind_speed: game.wind_speed,
													clouds: game.clouds
												};
											}
											console.log('Parsed weather data:', weatherData);
											return (
												<Row style={{ rowGap: '10px' }}>
													<Col lg={3} md={6} xs={12}>
														<div style={{ background: '#f0f9ff', border: '1px solid #e0f2fe', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
															<p style={{ margin: 0, color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>Condition</p>
															<p style={{ margin: '4px 0 0 0', fontWeight: 700, fontSize: '14px', color: '#0284c7' }}>{weatherData.weather_desc || weatherData.weather || 'N/A'}</p>
														</div>
													</Col>
													<Col lg={3} md={6} xs={12}>
														<div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
															<p style={{ margin: 0, color: '#92400e', fontSize: '11px', textTransform: 'uppercase' }}>Temperature</p>
															<p style={{ margin: '4px 0 0 0', fontWeight: 700, fontSize: '14px', color: '#d97706' }}>{weatherData.temp ? `${weatherData.temp}°C` : 'N/A'}</p>
														</div>
													</Col>
													<Col lg={3} md={6} xs={12}>
														<div style={{ background: '#ecfdf5', border: '1px solid #d1fae5', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
															<p style={{ margin: 0, color: '#065f46', fontSize: '11px', textTransform: 'uppercase' }}>Humidity</p>
															<p style={{ margin: '4px 0 0 0', fontWeight: 700, fontSize: '14px', color: '#059669' }}>{weatherData.humidity ? `${weatherData.humidity}%` : 'N/A'}</p>
														</div>
													</Col>
													<Col lg={3} md={6} xs={12}>
														<div style={{ background: '#f3e8ff', border: '1px solid #e9d5ff', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
															<p style={{ margin: 0, color: '#6b21a8', fontSize: '11px', textTransform: 'uppercase' }}>Wind Speed</p>
															<p style={{ margin: '4px 0 0 0', fontWeight: 700, fontSize: '14px', color: '#9333ea' }}>{weatherData.wind_speed ? `${weatherData.wind_speed} km/h` : 'N/A'}</p>
														</div>
													</Col>
													<Col lg={3} md={6} xs={12}>
														<div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
															<p style={{ margin: 0, color: '#991b1b', fontSize: '11px', textTransform: 'uppercase' }}>Visibility</p>
															<p style={{ margin: '4px 0 0 0', fontWeight: 700, fontSize: '14px', color: '#dc2626' }}>{weatherData.visibility ? `${weatherData.visibility} m` : 'N/A'}</p>
														</div>
													</Col>
													<Col lg={3} md={6} xs={12}>
														<div style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
															<p style={{ margin: 0, color: '#475569', fontSize: '11px', textTransform: 'uppercase' }}>Cloud Cover</p>
															<p style={{ margin: '4px 0 0 0', fontWeight: 700, fontSize: '14px', color: '#334155' }}>{weatherData.clouds ? `${weatherData.clouds}%` : 'N/A'}</p>
														</div>
													</Col>
												</Row>
											);
										} catch (e) {
											console.error('Error parsing weather data:', e);
											return <p style={{ color: '#6b7280' }}>Weather data unavailable</p>;
										}
									})()}
								</div>
							</Col>
						</Row>
					) : null}
					{/* Pitch Section */}
					{game?.pitch_json ? (
						<Row style={{ padding: '0 20px 20px 20px' }}>
							<Col lg={12}>
								<div style={{ background: '#fff', border: '1px solid #e6e6e6', borderRadius: '8px', padding: '14px 16px' }}>
									<h5 style={{ margin: '0 0 12px 0', fontWeight: 700, color: '#374151' }}>Pitch Report</h5>
									{(() => {
										try {
											const pitchData = typeof game.pitch_json === 'string' ? JSON.parse(game.pitch_json) : game.pitch_json;
											console.log('Parsed pitch data:', pitchData);
											return (
												<Row style={{ rowGap: '10px' }}>
													<Col lg={3} md={6} xs={12}>
														<div style={{ background: '#f0fdf4', border: '1px solid #dcfce7', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
															<p style={{ margin: 0, color: '#166534', fontSize: '11px', textTransform: 'uppercase' }}>Pitch Condition</p>
															<p style={{ margin: '4px 0 0 0', fontWeight: 700, fontSize: '14px', color: '#15803d' }}>{pitchData.pitch_condition || 'N/A'}</p>
														</div>
													</Col>
													<Col lg={3} md={6} xs={12}>
														<div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
															<p style={{ margin: 0, color: '#9a3412', fontSize: '11px', textTransform: 'uppercase' }}>Batting Condition</p>
															<p style={{ margin: '4px 0 0 0', fontWeight: 700, fontSize: '14px', color: '#ea580c' }}>{pitchData.batting_condition || 'N/A'}</p>
														</div>
													</Col>
													<Col lg={3} md={6} xs={12}>
														<div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
															<p style={{ margin: 0, color: '#713f12', fontSize: '11px', textTransform: 'uppercase' }}>Pace Bowling</p>
															<p style={{ margin: '4px 0 0 0', fontWeight: 700, fontSize: '14px', color: '#ca8a04' }}>{pitchData.pace_bowling_condition || 'N/A'}</p>
														</div>
													</Col>
													<Col lg={3} md={6} xs={12}>
														<div style={{ background: '#fdf4ff', border: '1px solid #e9d5ff', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
															<p style={{ margin: 0, color: '#6b21a8', fontSize: '11px', textTransform: 'uppercase' }}>Spin Bowling</p>
															<p style={{ margin: '4px 0 0 0', fontWeight: 700, fontSize: '14px', color: '#9333ea' }}>{pitchData.spine_bowling_condition || pitchData.spin_bowling_condition || 'N/A'}</p>
														</div>
													</Col>
												</Row>
											);
										} catch (e) {
											console.error('Error parsing pitch data:', e);
											return <p style={{ color: '#6b7280' }}>Pitch data unavailable</p>;
										}
									})()}
								</div>
							</Col>
						</Row>
					) : null}
					{(scorecardInnings.length || scorecardNotes.length) ? (
						<Row style={{ padding: '0 20px 20px 20px' }}>
							<Col lg={12}>
								<div style={{ background: '#fff', border: '1px solid #e6e6e6', borderRadius: '8px', padding: '14px 16px' }}>
									<button
										type='button'
										onClick={() => setShowScorecardSection(!showScorecardSection)}
										style={{ width: '100%', textAlign: 'left', border: 'none', background: 'transparent', padding: 0, marginBottom: '12px', fontWeight: 700, fontSize: '18px' }}
									>
										{showScorecardSection ? '▾' : '▸'} IPL Scorecard
									</button>
									{showScorecardSection ? scorecardInnings.map((inning) => {
										const batsmen = asArray(inning.batsmen);
										const bowlers = asArray(inning.bowlers);
										const fows = asArray(inning.fows);
										return (
											<div key={`inning-${inning.inning_no}`} style={{ marginBottom: '16px', border: '1px solid #f0f0f0', borderRadius: '6px', padding: '10px 12px', background: '#fafafa' }}>
												<p style={{ marginBottom: '6px', fontWeight: 700 }}>
													Inning {inning.inning_no}: {resolveTeamName(inning.batting_team_id)} ({inning.scores_full || inning.scores || 'N/A'})
												</p>
												<div style={{ overflowX: 'auto' }}>
													<table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse', marginBottom: '10px' }}>
														<thead>
															<tr>
																<th style={{ textAlign: 'left', padding: '6px 4px' }}>Batter</th>
																<th style={{ textAlign: 'right', padding: '6px 4px' }}>R</th>
																<th style={{ textAlign: 'right', padding: '6px 4px' }}>B</th>
																<th style={{ textAlign: 'right', padding: '6px 4px' }}>4s</th>
																<th style={{ textAlign: 'right', padding: '6px 4px' }}>6s</th>
																<th style={{ textAlign: 'right', padding: '6px 4px' }}>SR</th>
																<th style={{ textAlign: 'left', padding: '6px 4px' }}>How Out</th>
															</tr>
														</thead>
														<tbody>
															{batsmen.length ? batsmen.map((b, idx) => (
																<tr key={`bat-${inning.inning_no}-${idx}`}>
																	<td style={{ padding: '6px 4px' }}>
																		<div>
																			{pickValue(b, ['name', 'batsman_name', 'player_name', 'title'])}
																			{b.role_str && <span style={{ fontSize: '11px', color: '#666', marginLeft: '4px' }}>{b.role_str}</span>}
																		</div>
																		{b.position && <div style={{ fontSize: '11px', color: '#666' }}>{b.position}</div>}
																	</td>
																	<td style={{ textAlign: 'right', padding: '6px 4px' }}>{pickValue(b, ['runs', 'r'])}</td>
																	<td style={{ textAlign: 'right', padding: '6px 4px' }}>{pickValue(b, ['balls', 'balls_faced', 'b'])}</td>
																	<td style={{ textAlign: 'right', padding: '6px 4px' }}>{pickValue(b, ['fours', '4s', 'f'])}</td>
																	<td style={{ textAlign: 'right', padding: '6px 4px' }}>{pickValue(b, ['sixes', '6s', 's'])}</td>
																	<td style={{ textAlign: 'right', padding: '6px 4px' }}>{pickValue(b, ['strike_rate', 'sr'])}</td>
																	<td style={{ padding: '6px 4px', fontSize: '12px' }}>
																		{b.batting === 'true' ? (
																			<span style={{ color: '#28a745', fontWeight: 600 }}>Not out</span>
																		) : (
																			pickValue(b, ['how_out', 'dismissal'])
																		)}
																	</td>
																</tr>
															)) : (
																<tr><td colSpan={7} style={{ padding: '6px 4px' }}>No batting data</td></tr>
															)}
														</tbody>
													</table>
												</div>
												<div style={{ overflowX: 'auto' }}>
													<table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse', marginBottom: '10px' }}>
														<thead>
															<tr>
																<th style={{ textAlign: 'left', padding: '6px 4px' }}>Bowler</th>
																<th style={{ textAlign: 'right', padding: '6px 4px' }}>O</th>
																<th style={{ textAlign: 'right', padding: '6px 4px' }}>M</th>
																<th style={{ textAlign: 'right', padding: '6px 4px' }}>R</th>
																<th style={{ textAlign: 'right', padding: '6px 4px' }}>W</th>
																<th style={{ textAlign: 'right', padding: '6px 4px' }}>NB</th>
																<th style={{ textAlign: 'right', padding: '6px 4px' }}>WD</th>
																<th style={{ textAlign: 'right', padding: '6px 4px' }}>Econ</th>
															</tr>
														</thead>
														<tbody>
															{bowlers.length ? bowlers.map((bw, idx) => (
																<tr key={`bowl-${inning.inning_no}-${idx}`}>
																	<td style={{ padding: '6px 4px' }}>{pickValue(bw, ['name', 'bowler_name', 'player_name', 'title'])}</td>
																	<td style={{ textAlign: 'right', padding: '6px 4px' }}>{pickValue(bw, ['overs', 'o'])}</td>
																	<td style={{ textAlign: 'right', padding: '6px 4px' }}>{pickValue(bw, ['maidens', 'm'])}</td>
																	<td style={{ textAlign: 'right', padding: '6px 4px' }}>{pickValue(bw, ['runs_conceded', 'runs', 'r'])}</td>
																	<td style={{ textAlign: 'right', padding: '6px 4px' }}>{pickValue(bw, ['wickets', 'w'])}</td>
																	<td style={{ textAlign: 'right', padding: '6px 4px' }}>{pickValue(bw, ['noballs', 'nb'])}</td>
																	<td style={{ textAlign: 'right', padding: '6px 4px' }}>{pickValue(bw, ['wides', 'wd'])}</td>
																	<td style={{ textAlign: 'right', padding: '6px 4px' }}>{pickValue(bw, ['economy', 'econ'])}</td>
																</tr>
															)) : (
																<tr><td colSpan={8} style={{ padding: '6px 4px' }}>No bowling data</td></tr>
															)}
														</tbody>
													</table>
												</div>
												{/* Extra Runs Breakdown */}
												{inning.extra_runs && (
													<div style={{ marginTop: '10px', padding: '8px', background: '#e8f4fd', borderRadius: '4px' }}>
														<p style={{ margin: '0 0 4px 0', fontWeight: 600, fontSize: '13px', color: '#495057' }}>
															Extras:
														</p>
														<p style={{ margin: 0, fontSize: '12px', color: '#6c757d' }}>
															{inning.extra_runs.byes ? `Byes ${inning.extra_runs.byes}, ` : ''}
															{inning.extra_runs.legbyes ? `Leg byes ${inning.extra_runs.legbyes}, ` : ''}
															{inning.extra_runs.wides ? `Wides ${inning.extra_runs.wides}, ` : ''}
															{inning.extra_runs.noballs ? `No balls ${inning.extra_runs.noballs}, ` : ''}
															{inning.extra_runs.penalty && inning.extra_runs.penalty !== '0' ? `Penalty ${inning.extra_runs.penalty}, ` : ''}
															<strong>Total {inning.extra_runs.total || 0}</strong>
														</p>
													</div>
												)}
												{/* Did Not Bat */}
												{inning.did_not_bat && inning.did_not_bat.length > 0 && (
													<div style={{ marginTop: '10px', padding: '8px', background: '#fff3cd', borderRadius: '4px' }}>
														<p style={{ margin: '0 0 4px 0', fontWeight: 600, fontSize: '13px', color: '#495057' }}>
															Did not bat:
														</p>
														<p style={{ margin: 0, fontSize: '12px', color: '#6c757d' }}>
															{inning.did_not_bat.map(player => player.name).join(', ')}
														</p>
													</div>
												)}
												{/* Current Partnership */}
												{inning.current_partnership && inning.current_partnership.batsmen && (
													<div style={{ marginTop: '10px', padding: '8px', background: '#d1ecf1', borderRadius: '4px' }}>
														<p style={{ margin: '0 0 4px 0', fontWeight: 600, fontSize: '13px', color: '#495057' }}>
															Current Partnership:
														</p>
														<p style={{ margin: 0, fontSize: '12px', color: '#6c757d' }}>
															{inning.current_partnership.runs} runs ({inning.current_partnership.balls} balls, {inning.current_partnership.overs} overs)
															{inning.current_partnership.batsmen.length > 0 && (
																<> - {inning.current_partnership.batsmen.map(b => `${b.name} ${b.runs}*`).join(' & ')}</>
															)}
														</p>
													</div>
												)}
												{/* Fielding Statistics */}
												{inning.fielders && inning.fielders.length > 0 && (
													<div style={{ marginTop: '10px', padding: '8px', background: '#f8f9fa', borderRadius: '4px' }}>
														<p style={{ margin: '0 0 4px 0', fontWeight: 600, fontSize: '13px', color: '#495057' }}>
															Fielding:
														</p>
														<div style={{ fontSize: '12px', color: '#6c757d' }}>
															{inning.fielders.map(fielder => (
																<div key={fielder.fielder_id} style={{ marginBottom: '2px' }}>
																	<strong>{fielder.fielder_name}:</strong> 
																	{fielder.catches > 0 && ` ${fielder.catches} catch${fielder.catches > 1 ? 'es' : ''}`}
																	{fielder.stumping > 0 && ` ${fielder.stumping} stumping${fielder.stumping > 1 ? 's' : ''}`}
																	{fielder.runout_thrower > 0 && ` ${fielder.runout_thrower} runout throw`}
																	{fielder.runout_catcher > 0 && ` ${fielder.runout_catcher} runout catcher`}
																	{fielder.runout_direct_hit > 0 && ` ${fielder.runout_direct_hit} direct hit`}
																</div>
															))}
														</div>
													</div>
												)}
												{fows.length ? (
													<div style={{ marginTop: '10px', padding: '8px', background: '#f8f9fa', borderRadius: '4px' }}>
														<p style={{ margin: '0 0 4px 0', fontWeight: 600, fontSize: '13px', color: '#495057' }}>
															Fall of wickets:
														</p>
														<p style={{ margin: 0, fontSize: '12px', color: '#6c757d' }}>
															{fows.map((f, idx) => {
																if (typeof f === 'string') return f;
																if (typeof f === 'object') {
																	// Use the rich data format from the API
																	const name = pickValue(f, ['name', 'batsman', 'player']);
																	const scoreAtDismissal = pickValue(f, ['score_at_dismissal', 'runs', 'score']);
																	const oversAtDismissal = pickValue(f, ['overs_at_dismissal', 'over', 'overs']);
																	
																	if (name && scoreAtDismissal && oversAtDismissal) {
																		return `${scoreAtDismissal}/${oversAtDismissal} (${name})`;
																	}
																	// Fallback to other formats
																	const wicketText = pickValue(f, ['text', 'fow', 'wicket', 'description']);
																	const runs = pickValue(f, ['runs', 'score', 'team_score']);
																	const over = pickValue(f, ['over', 'overs']);
																	const player = pickValue(f, ['player', 'batsman', 'name']);
																	
																	if (wicketText) return wicketText;
																	if (runs && over) {
																		const playerText = player ? ` (${player})` : '';
																		return `${runs}/${over}${playerText}`;
																	}
																}
																return '';
															}).filter(Boolean).join(', ')}
														</p>
													</div>
												) : null}
											</div>
										);
									}) : null}
									{scorecardPlayers.length && showScorecardSection ? (
										<p style={{ marginTop: '10px', marginBottom: 0, color: '#666' }}>
											Players indexed: {scorecardPlayers.length}
										</p>
									) : null}
								</div>
							</Col>
						</Row>
					) : null}
					{scorecardNotes.length ? (
						<Row style={{ padding: '0 20px 20px 20px' }}>
							<Col lg={12}>
								<div style={{ background: '#fff', border: '1px solid #e6e6e6', borderRadius: '8px', padding: '14px 16px' }}>
									<button
										type='button'
										onClick={() => setShowNotesSection(!showNotesSection)}
										style={{ width: '100%', textAlign: 'left', border: 'none', background: 'transparent', padding: 0, marginBottom: showNotesSection ? '8px' : 0, fontWeight: 700, fontSize: '18px' }}
									>
										{showNotesSection ? '▾' : '▸'} Match Notes
									</button>
									{showNotesSection ? (
										<div style={{ marginTop: '8px' }}>
											{scorecardNotes.slice(0, 12).map((note) => (
												<p key={`note-${note.inning_no}-${note.note_index}`} style={{ marginBottom: '4px' }}>
													<strong>Inning {note.inning_no}:</strong> {note.note_text}
												</p>
											))}
										</div>
									) : null}
								</div>
							</Col>
						</Row>
					) : null}
				</div>
			</div>
		</section>
	);
}

const mapStateToProps = (state) => ({
	isLoading: state.isLoading,
	isLoaded: state.isLoaded,
	healthScores: state.healthScores,
	match: state.match,
	schedules: state.schedules,
	teams: state.teams,
	user: state.user
});

export default connect(mapStateToProps)(CricketMatchup);
